import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, Mail, MessageCircle, Linkedin, Instagram, Youtube, Facebook, MapPin } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { debounce } from '../../utils/performance';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showMiniBar, setShowMiniBar] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = debounce(() => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 50;

      // Detect if hero section (#home) is in view
      const heroEl = document.getElementById('home');
      let isHeroInView = false;
      if (heroEl) {
        const rect = heroEl.getBoundingClientRect();
        // If the bottom of hero is still below the top of the viewport, hero is in view
        isHeroInView = rect.bottom > 0;
      } else {
        // Fallback: treat near-top as hero visible
        isHeroInView = currentScrollY < 50 && window.location.pathname !== '/corporate';
      }

      // If hero is visible or mobile menu is open, keep navbar visible
      if (isHeroInView || isMenuOpen || window.location.pathname === '/corporate') {
        setIsVisible(true);
        setIsScrolled(scrolled);
        setLastScrollY(currentScrollY);
        setShowMiniBar(false);
        return;
      }

      // Hide navbar when scrolling down, show when scrolling up (with threshold)
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false); // Hide when scrolling down
      } else if (currentScrollY < lastScrollY && (lastScrollY - currentScrollY) > 50) {
        setIsVisible(true); // Show only when scrolling up significantly (50px threshold)
      }

      setIsScrolled(scrolled);
      setLastScrollY(currentScrollY);
      setShowMiniBar(currentScrollY > 180 && !isMenuOpen);
    }, 10);
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    // Run once to set initial state for standalone pages
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [lastScrollY, isMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      setShowMiniBar(false);
      setIsVisible(true);
      // Move focus into drawer
      setTimeout(() => {
        drawerRef.current?.focus();
      }, 0);
      return () => {
        document.body.style.overflow = original;
        // Return focus to menu button
        menuButtonRef.current?.focus();
      };
    }
  }, [isMenuOpen]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    if (isMenuOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'About us', href: '#about' },
    { name: 'Locate us', href: '#contact' },
    { name: 'Services', href: '#services' },
    { name: 'Corporate Wellness', href: '/corporate' },
    { name: 'Resources', href: '#resources' },
    { name: 'Contact us', href: '#contact' }
  ];

  const getActiveItemName = (): string | null => {
    if (typeof window === 'undefined') return null;
    if (window.location.pathname === '/corporate') return 'Corporate Wellness';
    const hash = window.location.hash || '#home';
    const match = navItems.find((n) => n.href === hash);
    return match ? match.name : null;
  };
  const activeItemName = getActiveItemName();

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (href: string) => {
    setIsMenuOpen(false);
    // Full-page route for corporate
    if (href.startsWith('/')) {
      window.location.assign(href);
      return;
    }
    // If we're not on the homepage and the link is an in-page anchor, navigate to the homepage with hash
    if (href.startsWith('#') && window.location.pathname !== '/') {
      window.location.assign('/' + href);
      return;
    }
    // Smooth scroll to section when already on homepage
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav 
      className={`fixed top-0 w-full ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white'
      } ${
        isMenuOpen
          ? 'transform-none z-[120]'
          : `transition-all duration-500 ease-in-out ${isVisible ? 'translate-y-0 z-50' : '-translate-y-full z-50'}`
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Top Section - Contact Information */}
      <div className="bg-white border-b border-gray-200 py-2 px-4" role="banner">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center" role="banner">
            <div className="flex-shrink-0">
              <img
                src="/images/logo.svg"
                alt="Arpan PhysioCure™ Logo®"
                className="h-12 w-auto sm:h-12 md:h-14 lg:h-16"
              />
            </div>
          </div>

          {/* Contact Information (Desktop only) */}
          <div className="hidden lg:flex flex-wrap justify-end items-center gap-2 sm:gap-3 lg:gap-4">
            {/* Call us */}
            <div className="flex items-center space-x-1.5 sm:space-x-2" role="group" aria-label="Phone contact">
              <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium hidden sm:block">Call us anytime</span>
                <span className="text-xs text-gray-500 font-medium sm:hidden">Call</span>
                <a 
                  href="tel:+917974434671" 
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  aria-label="Call us at +91 79744 34671"
                >
                  +91 79744 34671
                </a>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="flex items-center space-x-1.5 sm:space-x-2" role="group" aria-label="WhatsApp contact">
              <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm">
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium hidden sm:block">WhatsApp</span>
                <span className="text-xs text-gray-500 font-medium sm:hidden">WhatsApp</span>
                <a 
                  href="https://wa.me/917974434671" 
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  aria-label="Chat with us on WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  +91 79744 34671
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center space-x-1.5 sm:space-x-2" role="group" aria-label="Email contact">
              <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium hidden sm:block">Write a mail</span>
                <span className="text-xs text-gray-500 font-medium sm:hidden">Email</span>
                <a 
                  href="mailto:contact@arpanphysiocure.com" 
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  aria-label="Send us an email"
                >
                  contact@arpanphysiocure.com
                </a>
              </div>
            </div>
          </div>

          {/* Mobile quick actions */}
          <div className="flex items-center gap-2 lg:hidden">
            <a
              href="tel:+917974434671"
              aria-label="Call us"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-blue-600 hover:bg-blue-50"
            >
              <Phone className="w-4 h-4" />
            </a>
            <a
              href="https://wa.me/917974434671"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp us"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-green-600 hover:bg-green-50"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
            {/* Mobile menu button */}
            <button
              onClick={handleMenuToggle}
              ref={menuButtonRef}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:outline-none"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Section - Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center py-2 lg:py-3">
            {/* Desktop Navigation */}
            <div className="hidden lg:block" role="menubar">
              <div className="flex items-center space-x-6" role="none">
                {navItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(item.href);
                    }}
                    className={`text-sm font-medium transition-all duration-200 hover:text-blue-600 relative ${
                      item.name === 'Home' 
                        ? 'text-blue-600 font-semibold' 
                        : 'text-gray-700 hover:font-medium'
                    }`}
                    role="menuitem"
                    tabIndex={0}
                  >
                    {item.name}
                    {item.name === 'Home' && (
                      <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-blue-600"></div>
                    )}
                  </a>
                ))}
              </div>
            </div>

            {/* Social Media Icons */}
            <div className="hidden lg:flex items-center space-x-3">
              {/* LinkedIn Clinic */}
              <a 
                href="https://www.linkedin.com/company/arpan-physiocure" 
                className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors duration-200 group"
                aria-label="Follow Arpan PhysioCure on LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin className="w-4 h-4 text-white group-hover:scale-110 transition-transform" />
              </a>
              
              {/* Instagram */}
              <a 
                href="https://www.instagram.com/arpan.physiocure/" 
                className="w-8 h-8 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center hover:from-pink-500 hover:to-purple-600 transition-all duration-200 group"
                aria-label="Follow Arpan PhysioCure on Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="w-4 h-4 text-white group-hover:scale-110 transition-transform" />
              </a>
              
              {/* YouTube */}
              <a 
                href="https://www.youtube.com/channel/UCygeKSUZOxh2vJUbH3QDzYw" 
                className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-all duration-200 group"
                aria-label="Subscribe to Arpan PhysioCure on YouTube"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Youtube className="w-4 h-4 text-white group-hover:scale-110 transition-transform" />
              </a>
              
              {/* LinkedIn Dr. Deep */}
              <a 
                href="https://www.linkedin.com/in/dr-deep-maheshwari-pt-4b369a196" 
                className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-all duration-200 group"
                aria-label="Connect with Dr. Deep Maheshwari on LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin className="w-4 h-4 text-white group-hover:scale-110 transition-transform" />
              </a>
            </div>

            {/* placeholder to balance layout on desktop */}
            <div className="lg:hidden" />
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Full-screen overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[120] lg:hidden">
          {/* Scrim */}
          <div className="absolute inset-0 bg-black/45 transition-opacity duration-300 opacity-100" onClick={() => setIsMenuOpen(false)} />
          {/* Drawer */}
          <aside
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            ref={drawerRef}
            className="absolute right-0 top-0 h-full w-[84%] max-w-sm bg-white shadow-2xl flex flex-col overflow-y-auto transform transition-transform duration-300 translate-x-0"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <img src="/images/logo.svg" alt="Arpan PhysioCure" className="h-10" />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav className="px-3 py-2 space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={`block text-base font-semibold px-3 py-3 rounded-lg transition-colors ${
                    activeItemName === item.name ? 'text-blue-600 bg-blue-50' : 'text-gray-800 hover:bg-gray-100'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }}
                  role="menuitem"
                  tabIndex={0}
                >
                  {item.name}
                </a>
              ))}
            </nav>

            <div className="px-3 pt-1 pb-4 space-y-2 mt-auto">
              <a href="tel:+917974434671" className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg font-semibold text-sm">
                <Phone className="w-4 h-4" /> Call Now
              </a>
              <a href="https://wa.me/917974434671" target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 border-2 border-green-600 text-green-700 py-3 rounded-lg font-semibold text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href="https://maps.google.com/?q=55+Ambikapuri+60+Feet+Road+Aerodrome+Kalani+Nagar+Raj+Mohalla+Indore+452005" target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-800 py-3 rounded-lg font-semibold text-sm">
                <MapPin className="w-4 h-4" /> Directions
              </a>
              <div className="flex items-center justify-center gap-3 pt-1">
                <a href="https://www.linkedin.com/company/arpan-physiocure" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center">
                  <Linkedin className="w-4 h-4 text-white" />
                </a>
                <a href="https://www.instagram.com/arpan.physiocure/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                  <Instagram className="w-4 h-4 text-white" />
                </a>
                <a href="https://www.youtube.com/channel/UCygeKSUZOxh2vJUbH3QDzYw" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center">
                  <Youtube className="w-4 h-4 text-white" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Sticky mobile mini-bar */}
      {!isMenuOpen && showMiniBar && (
        <div className="lg:hidden fixed inset-x-0 bottom-4 sm:bottom-5 z-40 px-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm border border-gray-200 shadow-2xl rounded-full overflow-hidden">
              <div className="grid grid-cols-3 divide-x divide-gray-200">
                <a
                  href="tel:+917974434671"
                  className="flex items-center justify-center gap-2 py-3 text-blue-700 font-semibold hover:bg-blue-50"
                  aria-label="Call us"
                >
                  <Phone className="w-6 h-6" />
                  <span className="text-sm">Call</span>
                </a>
                <a
                  href="https://wa.me/917974434671"
              target="_blank"
              rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 text-green-700 font-semibold hover:bg-green-50"
                  aria-label="WhatsApp"
            >
                  <MessageCircle className="w-6 h-6" />
                  <span className="text-sm">WhatsApp</span>
            </a>
            <a 
                  href="https://maps.google.com/?q=55+Ambikapuri+60+Feet+Road+Aerodrome+Kalani+Nagar+Raj+Mohalla+Indore+452005"
              target="_blank"
              rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 text-gray-800 font-semibold hover:bg-gray-50"
                  aria-label="Directions"
            >
                  <MapPin className="w-6 h-6" />
                  <span className="text-sm">Directions</span>
            </a>
          </div>
        </div>
      </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;